import React, { useState } from 'react';
import type { MutableRefObject } from 'react';

import TableComponent from 'components/partials/table-component/table-component';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';

import useRelatedGoals from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/related-goals/hooks/use-related-goals';
import { getRelatedGoalsTableSettings } from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/related-goals/related-goals-table-settings';
import RelatedGoalsFilters from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/related-goals/related-goals-filters';

import {
  INITIAL_SORTING,
  type SortRule,
} from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/related-goals/related-goals.const';

interface RelatedGoalsProps {
  clientId: string | number | undefined;
  criticalElementId: string | number | undefined;
  enteringClassYear: string | number | undefined;
  isEditMode: boolean;
  onSave?: MutableRefObject<
    ((newId?: string | number) => Promise<void>) | undefined
  >;
}

const RelatedGoals = ({
  clientId,
  criticalElementId,
  enteringClassYear,
  isEditMode,
  onSave,
}: RelatedGoalsProps): React.JSX.Element => {
  const componentKey = `${criticalElementId || 'new'}-${enteringClassYear}`;

  const {
    filterOption,
    handleFilterChange,
    currentData,
    linkedGoalsIds,
    isCreation,
    showLoader,
    handleToggleChange,
    isToggling,
  } = useRelatedGoals({
    clientId,
    criticalElementId,
    enteringClassYear,
    isEditMode,
    onSave,
  });

  const columns = getRelatedGoalsTableSettings(
    linkedGoalsIds,
    handleToggleChange,
    isToggling || !isEditMode,
    clientId
  );

  const [sortingParams, setSortingParams] =
    useState<SortRule[]>(INITIAL_SORTING);

  return (
    <div className="relative w-full" key={componentKey}>
      <div className="flex justify-between items-center mb-2.5">
        <p className="font-semibold text-gray-700 leading-5">Linked goals</p>
      </div>

      <p className="text-sm text-gray-600 mb-3">
        Define the goals that are supported by the critical element.
      </p>

      {criticalElementId && (
        <RelatedGoalsFilters
          filterOption={filterOption}
          handleFilterChange={handleFilterChange}
          isCreation={isCreation}
        />
      )}

      <div className="relative">
        {!currentData?.length && !showLoader ? (
          <EmptySpaceContent
            text="No goals have been linked yet."
            rotation="horizontal"
            customClass="!h-[200px]"
          />
        ) : (
          <TableComponent
            columns={columns}
            data={currentData}
            isLoading={showLoader}
            sortingParams={sortingParams}
            setSortingParams={setSortingParams}
            customHeight="max-h-[50vh]"
            pagination={false}
            showingRecordsTextIsOn={false}
            viewByAmountSelectIsOn={false}
          />
        )}
      </div>
    </div>
  );
};

export default RelatedGoals;
